import { orderNavItems } from '../../lib/navOrder';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth.store';
import { hasAdminPermission, type AdminPermissionKey } from '../../lib/adminPermissions';

interface NavItem {
  label: string;
  to: string;
  permission: AdminPermissionKey;
}

const NAV_ITEMS: NavItem[] = [
  { label: '首页', to: '/', permission: 'dashboard' },
  { label: '邀请码策略', to: '/settings/invitation-policy', permission: 'settings' },
  { label: '邀请规则', to: '/settings/invitation-rules', permission: 'settings' },
  { label: '人机验证', to: '/settings/captcha', permission: 'settings' },
  { label: 'OCR 识别配置', to: '/settings/ocr', permission: 'settings' },
  { label: '验证码邮件', to: '/settings/verification-email', permission: 'settings' },
  { label: '邀请奖励策略', to: '/settings/commission-policy', permission: 'settings' },
  { label: '新人注册奖励', to: '/settings/newcomer-reward', permission: 'settings' },
  { label: '市场配置', to: '/settings/markets', permission: 'settings' },
  { label: '提现策略', to: '/settings/withdrawal-policy', permission: 'settings' },
  { label: '任务风控配置', to: '/settings/task-risk-policy', permission: 'settings' },
  { label: '提现风控配置', to: '/settings/withdrawal-risk-policy', permission: 'settings' },
  { label: '自动打款设置', to: '/settings/withdrawal-payout', permission: 'settings' },
  { label: '存储桶配置', to: '/settings/media-storage', permission: 'settings' },
  { label: '红包提现配置', to: '/settings/withdrawal-red-packet', permission: 'settings' },
  { label: '首页内容配置', to: '/settings/home-content', permission: 'settings' },
  { label: '在线客服配置', to: '/settings/customer-service', permission: 'settings' },
  { label: '常见问题配置', to: '/settings/faq', permission: 'settings' },
  { label: 'App 更新配置', to: '/settings/app-update', permission: 'settings' },
  { label: 'Apple 登录配置', to: '/settings/apple-auth', permission: 'settings' },
  { label: '短信审核配置', to: '/settings/message-review', permission: 'settings' },
  { label: '法律文档', to: '/settings/legal-documents', permission: 'settings' },
  { label: '后台账号', to: '/admin-accounts', permission: 'admin_accounts' },
  { label: '用户管理', to: '/users', permission: 'users' },
  { label: '任务管理', to: '/tasks', permission: 'tasks' },
  { label: '奖励记录', to: '/rewards', permission: 'rewards' },
  { label: '消息通知', to: '/notifications', permission: 'notifications' },
  { label: '客服反馈', to: '/support-feedback', permission: 'support_feedback' },
  { label: '会员提交审核', to: '/assignments', permission: 'assignments.view' },
  { label: '提现管理', to: '/withdrawals', permission: 'withdrawals.view' },
  { label: '提现账号管理', to: '/withdrawal-accounts', permission: 'withdrawals' },
  { label: '下载任务限制名单', to: '/download-only-task-users', permission: 'withdrawals' },
  { label: '出款统计', to: '/withdrawal-statistics', permission: 'withdrawals' },
  { label: '提现账号风控', to: '/withdrawal-target-risk', permission: 'withdrawals' },
  { label: '邀请码管理', to: '/invitation/codes', permission: 'invitation' },
  { label: '邀请奖励记录', to: '/commissions', permission: 'commissions' },
  { label: '团队查询', to: '/team', permission: 'team' },
  { label: '商户管理', to: '/agents', permission: 'agents.view' },
  { label: '商户资金', to: '/agent-finance', permission: 'agent_finance.view' },
  { label: '商户任务设置', to: '/agent-task-settings', permission: 'agents.view' },
  { label: '商户任务发布审核', to: '/agent-task-reviews', permission: 'agent_tasks.review' },
  { label: '全部商户任务', to: '/agent-tasks', permission: 'agent_tasks.view' },
  { label: '商户帮助与反馈', to: '/agent-feedback', permission: 'agent_tasks.view' },
  { label: '商户在线报表', to: '/agent-reports', permission: 'agent_reports.view' },
  { label: '商户审计日志', to: '/agent-audit', permission: 'agent_logs.view' },
  { label: '广告管理', to: '/advertising', permission: 'advertising.view' },
  { label: '广告发布审核', to: '/advertising/reviews', permission: 'advertising.review' },
  { label: '广告位配置', to: '/advertising/slots', permission: 'advertising.view' },
  { label: '广告统计报表', to: '/advertising/reports', permission: 'advertising.reports.view' },
  { label: '系统日志', to: '/system-logs', permission: 'system_logs' },
];

const NAV_ORDER_STORAGE_KEY = 'aiptyg-admin-sidebar-order-v1';

function loadNavOrder(): string[] {
  try {
    const raw = window.localStorage.getItem(NAV_ORDER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return Array.from(new Set(parsed.filter((value) => typeof value === 'string')));
    }
    if (parsed && typeof parsed === 'object') {
      const legacyPaths = Object.values(parsed)
        .flatMap((value) => (Array.isArray(value) ? value : []))
        .filter((value): value is string => typeof value === 'string');
      return Array.from(new Set(legacyPaths));
    }
    return [];
  } catch {
    return [];
  }
}

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const [navOrder, setNavOrder] = useState<string[]>(loadNavOrder);
  const [draggingPath, setDraggingPath] = useState<string | null>(null);
  const items = orderNavItems(
    NAV_ITEMS.filter((item) => hasAdminPermission(user, item.permission)),
    navOrder,
  );

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const saveNavOrder = (paths: string[]) => {
    setNavOrder(paths);
    window.localStorage.setItem(NAV_ORDER_STORAGE_KEY, JSON.stringify(paths));
  };

  return (
    <aside className="flex flex-col w-60 h-full min-h-0 bg-white border-r border-border shrink-0">
      <div className="px-5 py-5">
        <span className="text-base font-semibold text-gray-900">体验官总管理后台</span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3">
        <ul className="space-y-0.5">
          {items.map((item) => (
            <li
              key={item.to}
              draggable
              onDragStart={() => setDraggingPath(item.to)}
              onDragOver={(event) => {
                if (draggingPath) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (!draggingPath || draggingPath === item.to) {
                  setDraggingPath(null);
                  return;
                }
                const current = items.map((navItem) => navItem.to);
                const from = current.indexOf(draggingPath);
                const to = current.indexOf(item.to);
                if (from >= 0 && to >= 0) {
                  const next = [...current];
                  const [moved] = next.splice(from, 1);
                  next.splice(to, 0, moved);
                  saveNavOrder(next);
                }
                setDraggingPath(null);
              }}
              onDragEnd={() => setDraggingPath(null)}
            >
              <NavLink
                to={item.to}
                end={item.to === '/' || item.to === '/advertising'}
                onClick={(event) => {
                  if (location.pathname !== item.to) return;
                  event.preventDefault();
                  navigate(`${item.to}${location.search}`, { replace: true, state: { refreshAt: Date.now() } });
                }}
                className={({ isActive }) =>
                  `block cursor-pointer border-l-2 px-3 py-2 rounded-md text-sm transition-all duration-150 ${
                    isActive
                      ? 'bg-primary/10 text-primary font-medium border-primary shadow-sm'
                      : 'border-transparent text-gray-700 hover:bg-primary/10 hover:text-gray-950 hover:border-primary/50 hover:shadow-sm'
                  }`
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="px-4 py-3 border-t border-border">
        <div className="text-sm text-gray-700 truncate">{user?.email ?? '-'}</div>
        <button
          onClick={handleLogout}
          className="mt-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          退出登录
        </button>
      </div>
    </aside>
  );
}
